import { useTranslation } from 'react-i18next';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Plus, Trash2, CalendarDays, Loader2, Edit } from 'lucide-react';
import { toast } from 'sonner';
import { API, AVAILABILITY_TYPES, getAvailabilityTypeInfo, formatDateDDMMYYYY } from './utils/helpers';

const MyAvailabilityTab = ({ currentUser, isAdmin }) => {
  const { t } = useTranslation();
  const [availability, setAvailability] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState({
    start_date: '',
    end_date: '',
    availability_type: 'vacation',
    reason: ''
  });

  const fetchMyAvailability = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/availability`);
      setAvailability(response.data);
    } catch (error) {
      console.error('Error fetching availability:', error);
      toast.error(t('auto.ctr.failedToLoadAvailability'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMyAvailability();
  }, [fetchMyAvailability]);

  const resetForm = () => {
    setForm({ start_date: '', end_date: '', availability_type: 'vacation', reason: '' });
  };

  const handleSubmit = async () => {
    if (!form.start_date || !form.end_date) {
      toast.error(t('auto.ctr.pleaseSelectStartAndEndDates'));
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/availability`, {
        start_date: form.start_date,
        end_date: form.end_date,
        availability_type: form.availability_type,
        reason: form.reason || null
      });
      toast.success(isAdmin ? t('auto.ctr.availabilityCreated') : t('auto.ctr.requestSubmittedForApproval'));
      setDialogOpen(false);
      resetForm();
      fetchMyAvailability();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setForm({
      start_date: item.start_date.split('T')[0],
      end_date: item.end_date.split('T')[0],
      availability_type: item.availability_type,
      reason: item.reason || ''
    });
    setEditDialogOpen(true);
  };

  const handleUpdate = async () => {
    if (!editingItem || !form.start_date || !form.end_date) {
      toast.error(t('auto.ctr.pleaseFillAllRequiredFields'));
      return;
    }

    setSubmitting(true);
    try {
      await axios.put(`${API}/availability/${editingItem.id}`, {
        start_date: form.start_date,
        end_date: form.end_date,
        availability_type: form.availability_type,
        reason: form.reason || null
      });
      toast.success(t('auto.ctr.availabilityUpdated'));
      setEditDialogOpen(false);
      setEditingItem(null);
      resetForm();
      fetchMyAvailability();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('auto.ctr.areYouSureYouWantTo'))) return;
    
    try {
      await axios.delete(`${API}/availability/${id}`);
      toast.success(t('auto.ctr.requestDeleted'));
      fetchMyAvailability();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete request');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-100 text-green-800">{t('auto.ctr.approved')}</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-800">{t('auto.ctr.rejected')}</Badge>;
      default:
        return <Badge className="bg-yellow-100 text-yellow-800">{t('auto.ctr.pending')}</Badge>;
    }
  };

  const canEditOrDelete = (item) => {
    return isAdmin || item.status === 'pending';
  };

  if (loading) {
    return <div className="flex items-center justify-center p-8"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div className="min-w-0">
              <CardTitle className="flex items-center gap-2">
                <CalendarDays className="h-5 w-5" />
                {isAdmin ? t('auto.ctr.allUnavailability') : t('auto.ctr.myUnavailability')}
              </CardTitle>
              <CardDescription>
                {isAdmin
                  ? t('manpower.unavailability.adminHint')
                  : t('manpower.unavailability.ownHint')}
              </CardDescription>
            </div>
            <Button onClick={() => { resetForm(); setDialogOpen(true); }} data-testid="add-unavailability-btn">
              <Plus className="h-4 w-4 mr-2" /> {t('auto.ctr.addUnavailability')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {availability.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <CalendarDays className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{t('auto.ctr.noUnavailabilityPeriodsSubmittedYet')}</p>
              <p className="text-sm mt-1">Click &quot;Add Unavailability&quot; to submit a request.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  {isAdmin && <TableHead>{t('auto.ctr.employee')}</TableHead>}
                  <TableHead>{t('auto.ctr.period')}</TableHead>
                  <TableHead>{t('auto.ctr.type')}</TableHead>
                  <TableHead>{t('auto.ctr.reason')}</TableHead>
                  <TableHead>{t('auto.ctr.status')}</TableHead>
                  <TableHead className="text-right">{t('auto.ctr.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {availability.map((item) => (
                  <TableRow key={item.id}>
                    {isAdmin && (
                      <TableCell className="font-medium">
                        {item.employee_name || 'Unknown'}
                      </TableCell>
                    )}
                    <TableCell>
                      {formatDateDDMMYYYY(item.start_date)} - {formatDateDDMMYYYY(item.end_date)}
                    </TableCell>
                    <TableCell>
                      <Badge className={getAvailabilityTypeInfo(item.availability_type).color}>
                        {getAvailabilityTypeInfo(item.availability_type).label}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-xs truncate">{item.reason || '-'}</TableCell>
                    <TableCell>{getStatusBadge(item.status)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {canEditOrDelete(item) && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(item)}
                              className="text-blue-600 hover:text-blue-700"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(item.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        {item.status === 'rejected' && item.rejection_reason && (
                          <span className="text-sm text-red-600 ml-2" title={item.rejection_reason}>
                            ⓘ
                          </span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add Unavailability Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('auto.ctr.addUnavailabilityPeriod')}</DialogTitle>
            <DialogDescription>
              Submit a period when you will be unavailable. {!isAdmin && 'This will require admin approval.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('auto.ctr.startDate')}</Label>
                <Input
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm(prev => ({ ...prev, start_date: e.target.value  }))}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('auto.ctr.endDate')}</Label>
                <Input
                  type="date"
                  value={form.end_date}
                  onChange={(e) => setForm(prev => ({ ...prev, end_date: e.target.value  }))}
                  min={form.start_date}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('auto.ctr.type')}</Label>
              <Select value={form.availability_type} onValueChange={(v) => setForm(prev => ({ ...prev, availability_type: v  }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AVAILABILITY_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('auto.ctr.reasonOptional')}</Label>
              <Textarea
                value={form.reason}
                onChange={(e) => setForm(prev => ({ ...prev, reason: e.target.value  }))}
                placeholder={t('auto.ctr.enterReasonForUnavailability')}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="dialog-cancel-btn">{t('auto.ctr.cancel')}</Button>
            <Button onClick={handleSubmit} disabled={submitting} data-testid="dialog-submit-btn">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {isAdmin ? t('auto.ctr.create') : t('auto.ctr.submitRequest')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Unavailability Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('auto.ctr.editUnavailability')}</DialogTitle>
            <DialogDescription>
              {t('auto.ctr.updateTheUnavailabilityPeriodDetails')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('auto.ctr.startDate')}</Label>
                <Input
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm(prev => ({ ...prev, start_date: e.target.value  }))}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('auto.ctr.endDate')}</Label>
                <Input
                  type="date"
                  value={form.end_date}
                  onChange={(e) => setForm(prev => ({ ...prev, end_date: e.target.value  }))}
                  min={form.start_date}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('auto.ctr.type')}</Label>
              <Select value={form.availability_type} onValueChange={(v) => setForm(prev => ({ ...prev, availability_type: v  }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AVAILABILITY_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('auto.ctr.reasonOptional')}</Label>
              <Textarea
                value={form.reason}
                onChange={(e) => setForm(prev => ({ ...prev, reason: e.target.value  }))}
                placeholder={t('auto.ctr.enterReasonForUnavailability')}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} data-testid="dialog-cancel-btn">{t('auto.ctr.cancel')}</Button>
            <Button onClick={handleUpdate} disabled={submitting} data-testid="dialog-update-btn">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyAvailabilityTab;
